import PropTypes from "prop-types";
import React from "react";

// Third Party
import moment from "moment";

// React Query
import { useMutation } from "@tanstack/react-query";

// API's
import { addNotification } from "@/Common/api/notifications/mutations";
import {
	createPayPlanAssignment,
	removePayPlanAssignmentInUser,
	resendSignRequestEmailMutation,
	updatePayPlanAssignment,
} from "@/PayPlans/api/assignments/mutations";

// Material UI
import { Stack } from "@mui/material";

// Custom Components
import PayPlanTable from "./PayPlanTable";
import { hasEditAccess } from "@/PayPlans/helpers/Access";
import { and, or } from "@Common/helpers/sonar";
import { useTermsAndConditionEdition } from "@/PayPlans/hooks/useTermsAndCondition";
import { DATE_FORMAT } from "@/Constants/date";
import useCanAssignPayPlan from "../../Assignments/hooks/useCanAssignPayPlan";

const headerProps = [
	{ title: "Pay Plan ID", width: "130px" },
	{ title: "Assignment Date", width: "180px" },
	{ title: "Assigned By" },
	{ title: "Effective Date", width: "240px" },
	{ title: "Acknowledgement Status", width: "280px" },
];

const getAssignedMessage = (payPlanName, effectiveDate) =>
	`<span title="" >You have been assigned pay plan <a title="Click Link" href="/">${payPlanName}</a> effective ${effectiveDate}.</span>`;

const getAcknowledgementStatus = (assignment = null) => assignment?.latestPayPlanAcknowledgementStatus?.status;

/**
 * @component Pay Plan Section Table
 * @param props
 * @returns {Element}
 * @constructor
 */
const PayPlanSection = (props) => {
	const [buttonVisible, setButtonVisible] = React.useState(false);
	const [open, setOpen] = React.useState(false);
	const [expanded, setExpanded] = React.useState(false);
	const [updateUpcomingPayPlan, setUpdateUpcomingPayPlan] = React.useState(false);

	// Auth
	const isEditable = useCanAssignPayPlan(props?.data?.position);

	const { payPlanAvailability } = useTermsAndConditionEdition(props?.data?.position);
	/*********************************************** MUTATIONS ***********************************************/
	const { mutate: mutateAddAssignment, isPending: addingAssignment } = useMutation({
		mutationFn: (input) =>
			createPayPlanAssignment({ input, locationId: props.locationId, userId: [props.data._id], planId: props?.payPlanSelected }),
	});

	const { mutate: mutatePayPlanAssignment } = useMutation({
		mutationKey: ["payPlanAssignmentUserInformation", props?.data?._id],
		mutationFn: (input) => {
			props.setShowRowFeedback(true);
			const { locationId, packageId, status, user, payPlan, effectiveDate } = input;
			return updatePayPlanAssignment(locationId, packageId, status, user, payPlan, effectiveDate);
		},
	});

	const { mutate: mutateRemoveAssignment, isPending: removingAssignment } = useMutation({
		mutationFn: () => {
			const packageIdToDelete = props?.data?.upcomingPayPlanAssignment?.packageId;
			return removePayPlanAssignmentInUser(props?.locationId, props?.data?._id, packageIdToDelete);
		},
	});
	const { mutate: resendSignRequest } = useMutation({
		mutationKey: ["resendSignRequestEmail", props?.data?.upcomingPayPlanAssignment?.packageId],
		mutationFn: async () => {
			const packageIdToResendEmail = props?.data?.upcomingPayPlanAssignment?.packageId;
			return resendSignRequestEmailMutation(props?.locationId, packageIdToResendEmail);
		},
	});

	/*********************************************** HANDLERS ***********************************************/
	const handleClick = (value) => {
		setOpen(true);
		setUpdateUpcomingPayPlan(typeof value === "boolean" ? Boolean(value) : false);
	};

	const handleClose = () => {
		setOpen(false);
		setUpdateUpcomingPayPlan(false);
	};
	const handleDeleteClick = () => {
		setButtonVisible(true);
	};
	const deletePlan = () => {
		setButtonVisible(false);

		setExpanded(!expanded);

		try {
			mutateRemoveAssignment();
		} catch (err) {
			console.error(err);
		}
	};
	const sendEmail = async () => {
		resendSignRequest();
		await addNotification(props?.locationId, props.data._id, {
			createdBy: "system",
			message: getAssignedMessage(
				props.data.upcomingPayPlanAssignment.planId.name,
				props.data.upcomingPayPlanAssignment.effectiveDate
			),
		});
	};

	const assignPayPlan = (input) => {
		mutateAddAssignment(input);
		handleClose();
	};

	const handleUpdatePayPlan = (input) => {
		mutatePayPlanAssignment(input);
		handleClose();
	};

	const upcomingData = props?.data?.upcomingPayPlanAssignment;
	const payPlanButtonText = !upcomingData ? "Assign New" : "Change Pay Plan";
	const latestCurrentStatus = getAcknowledgementStatus(props?.data?.currentPayPlanAssignment);
	const latestUpcomingStatus = getAcknowledgementStatus(props?.data?.upcomingPayPlanAssignment);

	let hireDate = null;

	if (and(!props?.data?.currentPayPlanAssignment, props?.data?.hireDate)) {
		// use same formatting used in the table's date pickers
		hireDate = moment.utc(props?.data?.hireDate).format(DATE_FORMAT.monthAbbrDatePadded);
	}

	let isSimpleButtonDisabled =
		!upcomingData?.planId?.pdfAvailability && upcomingData?.latestPayPlanAcknowledgementStatus?.status === "DRAFT";

	if (payPlanAvailability && (!payPlanAvailability?._id || payPlanAvailability?._id === upcomingData?.planId?._id)) {
		isSimpleButtonDisabled = !payPlanAvailability?.pdfAvailability;
	}

	return (
		<Stack spacing={5} marginTop={4}>
			<PayPlanTable
				payPlanAssignment={props?.data?.upcomingPayPlanAssignment}
				hireDate={hireDate}
				payPlanPosition={props?.data?.position}
				tableWidth="100%"
				title="Upcoming Pay Plan"
				deleteBtn
				changePayPlanBtn
				payPlanButtonText={payPlanButtonText}
				commissionCycle={props.commissionCycle}
				isLoading={or(props.loading, removingAssignment, addingAssignment)}
				headers={headerProps}
				disableSendEmail={or(!isEditable, latestUpcomingStatus === "COMPLETED", latestUpcomingStatus === "DECLINED")}
				sendEmailMethod={sendEmail}
				isModifiable={true}
				isSimpleButtonDisabled={isSimpleButtonDisabled}
				sx={{ marginTop: "50px" }}
				//all this if it has change pay plan button
				disableChangePayPlanBtn={
					!isEditable || (latestUpcomingStatus && latestUpcomingStatus !== "COMPLETED" && latestUpcomingStatus !== "DECLINED")
				}
				changePayPlanBtnClass={props.isMenuOpen ? "select-plan-dialog-assign-btn-open" : "select-plan-dialog-assign-btn"}
				onClickChangePayPlan={handleClick}
				openDialog={open}
				handleCloseDialog={handleClose}
				user={props.data}
				assignPayPlan={assignPayPlan}
				updateSelectedPlan={handleUpdatePayPlan}
				updateUpcomingPayPlan={updateUpcomingPayPlan}
				payPlanSelected={props.payPlanSelected}
				phone={props.phone}
				email={props.email}
				showRowFeedback={props.showRowFeedback}
				//delete button props
				visibleDlt={!buttonVisible && Boolean(props?.data?.upcomingPayPlanAssignment)}
				onClickAway={() => setButtonVisible(false)}
				handleDeleteClick={handleDeleteClick}
				deletePlan={deletePlan}
				noDataMessage="No upcoming pay plan assignment"
				removeDisabled={!isEditable}
			/>

			<PayPlanTable
				payPlanAssignment={props?.data?.currentPayPlanAssignment}
				payPlanPosition={props?.data?.position}
				tableWidth="100%"
				title="Current Pay Plan"
				headersInBlue={true}
				visibleFrame={true}
				isLoading={Boolean(props.loading)}
				headers={headerProps}
				disableSendEmail={or(!isEditable, latestCurrentStatus === "COMPLETED", latestCurrentStatus === "DECLINED")}
				sendEmailMethod={sendEmail}
				changePayPlanBtn={false}
				//all this if it has change pay plan button
				onClickChangePayPlan={handleClick}
				openDialog={open}
				handleCloseDialog={handleClose}
				user={props.data}
				assignPayPlan={assignPayPlan}
				payPlanSelected={props.payPlanSelected}
				phone={props.phone}
				email={props.email}
				showRowFeedback={false}
				noDataMessage="No current pay plan assignment"
				removeDisabled={!isEditable}
			/>
		</Stack>
	);
};
PayPlanSection.propTypes = {
	data: PropTypes.object,
	locationId: PropTypes.string,
	phone: PropTypes.string,
	email: PropTypes.string,
	showRowFeedback: PropTypes.bool,
	payPlanSelected: PropTypes.string,
	commissionCycle: PropTypes.object,
	loading: PropTypes.bool,
	isMenuOpen: PropTypes.bool,
	setShowRowFeedback: PropTypes.func,
};

export default PayPlanSection;
